import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { SnackbarCustomService } from '../../../../shared/components/snackbar/snackbar-custom.service'
import { ResponseRequestModel } from '../../../../core/models/response.model'
import { MatInputModule } from '@angular/material/input'
import { MatOption } from '@angular/material/core'
import { BankService } from '../../../../core/services/bank/bank/bank.service'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { BehaviorSubject, map, Observable, startWith } from 'rxjs'
import { AsyncPipe } from '@angular/common'
import { removeAccents } from '../../../../shared/utils/utils'

@Component({
  selector: 'bank-details-client-form',
  imports: [
    FormsModule,
    MatButton,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatInputModule,
    MatOption,
    MatAutocompleteModule,
    AsyncPipe,
  ],
  templateUrl: './bank-details-client-form.component.html',
  styleUrl: './bank-details-client-form.component.scss',
  providers: [BankService],
  standalone: true,
})
export class BankDetailsClientFormComponent implements OnInit, OnChanges {
  constructor(
    private formBuilder: FormBuilder,
    private snackbar: SnackbarCustomService,
    private bankService: BankService,
  ) {}

  form: FormGroup = new FormGroup({})
  loading = false

  @Output() response = new EventEmitter()
  @Input() editItem: any = {}
  @Input() optional = false

  private listTypeSubject = new BehaviorSubject<any[]>([])
  listType$ = this.listTypeSubject.asObservable()
  listTypeFiltered: Observable<any> | undefined

  ngOnInit(): void {
    this.getListType()
    this.createForm()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['editItem']) {
      this.treatmentItem()
    }
  }

  private treatmentItem() {
    this.resetForm()
    if (this.checkEdit()) {
      this.fillForm()
    }
  }

  displayFn = (id: number): string => {
    const listType = this.listTypeSubject.getValue()
    if (!listType || !id) {
      return ''
    }
    const selectedBank = listType.find((ele) => ele.id === id)
    return selectedBank ? selectedBank.descricao : ''
  }

  private _filter(value: any): any[] {
    const listType = this.listTypeSubject.getValue()
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase()
      return listType.filter(
        (ele) =>
          removeAccents(ele.descricao.toLowerCase()).includes(filterValue) ||
          ele.cod_banco.toLowerCase().includes(filterValue),
      )
    } else {
      return listType
    }
  }

  getListType() {
    this.bankService.listBank().subscribe({
      next: (response: ResponseRequestModel) => {
        const listType = response.data.map((item: any) => ({
          id: item.id,
          cod_banco: item.codigoBanco,
          descricao: item.nome,
        }))
        this.listTypeSubject.next(listType)

        this.listTypeFiltered = this.form.get('bank')!.valueChanges.pipe(
          startWith(''),
          map((value) => this._filter(value)),
        )
      },
      error: (err: ResponseRequestModel) => {
        console.error(err)
      },
      complete: () => {},
    })
  }

  checkBank() {
    const bank = this.form.get('bank')?.value
    if (bank && typeof bank == 'number') {
      return true
    } else {
      return false
    }
  }

  checkEdit() {
    return this.editItem && this.editItem.idCreate
  }

  fillForm() {
    if (this.checkEdit()) {
      this.form.patchValue({
        bank: this.editItem.banco,
        bank_branch: this.editItem.agencia,
        bank_account: this.editItem.conta,
        pix: this.editItem.pix,
      })
    }
  }

  resetForm() {
    this.form.reset()
  }

  createForm(): void {
    const req = this.optional ? [] : [Validators.required]
    this.form = this.formBuilder.group({
      bank: ['', req],
      bank_branch: ['', req],
      bank_account: ['', req],
      pix: [''],
    })
    this.listTypeFiltered = this.form.get('bank')!.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value)),
    )
  }

  getTypeDescription() {
    const typeId = this.form.get('bank')?.value
    if (typeId) {
      const listType = this.listTypeSubject.getValue()
      const element = listType.find((ele: any) => ele.id === typeId)
      return element ? element.descricao : ''
    }
    return ''
  }

  createAddress() {
    this.loading = true
    const model = this.createModel()
    this.response.emit({ action: 'create', item: model })
    this.resetForm()
    this.loading = false
  }

  editAddress() {
    this.loading = true
    const model = this.createModel(true)
    this.response.emit({ action: 'edit', item: model })
    this.resetForm()
    this.loading = false
  }

  deleteAddress() {
    this.loading = true
    const model = this.createModel(true)
    this.response.emit({ action: 'delete', item: model })
    this.resetForm()
    this.loading = false
  }

  cancel() {
    this.response.emit({ action: 'cancel' })
    this.resetForm()
  }

  createModel(edit = false) {
    return {
      ...(edit && { idCreate: this.editItem.idCreate }),
      banco: this.form.controls['bank'].value,
      bancoDesc: this.getTypeDescription(),
      agencia: this.form.controls['bank_branch'].value,
      conta: this.form.controls['bank_account'].value,
      pix: this.form.controls['pix'].value,
    }
  }
}
